import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error: error,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error details
    console.error('[ErrorBoundary] Error caught:', {
      error: error,
      errorInfo: errorInfo,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Update state with error info
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // You could also log the error to an error reporting service here
    // logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    // Reset the error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });

    // Reload the page as a fallback
    window.location.reload();
  };

  handleGoHome = () => {
    // Reset the error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });

    // Navigate to home/dashboard
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorId } = this.state;
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl border border-red-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-6">
              <div className="flex items-center">
                <div className="bg-white bg-opacity-20 rounded-full p-3 mr-4">
                  <AlertTriangle className="text-white" size={32} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white mb-1">
                    Une erreur s'est produite
                  </h1>
                  <p className="text-red-100 text-sm">
                    L'application a rencontré un problème inattendu
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-8 py-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  Que s'est-il passé ?
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  Une erreur technique s'est produite dans l'application EBIOS RM. 
                  Nos équipes ont été automatiquement notifiées et travaillent à résoudre ce problème.
                </p>
              </div>

              {/* Error Details (collapsible) */}
              <details className="mb-6 bg-gray-50 rounded-lg border border-gray-200">
                <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  Détails techniques (pour les développeurs)
                </summary>
                <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                        ID d'erreur
                      </h4>
                      <code className="text-xs bg-gray-200 px-2 py-1 rounded font-mono text-gray-800">
                        {errorId}
                      </code>
                    </div>
                    
                    {error && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                          Message d'erreur
                        </h4>
                        <code className="text-xs bg-red-100 px-2 py-1 rounded font-mono text-red-800 block">
                          {error.toString()}
                        </code>
                      </div>
                    )}

                    {errorInfo && errorInfo.componentStack && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                          Stack trace des composants
                        </h4>
                        <pre className="text-xs bg-gray-200 px-2 py-1 rounded font-mono text-gray-800 overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}

                    {error && error.stack && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                          Stack trace JavaScript
                        </h4>
                        <pre className="text-xs bg-gray-200 px-2 py-1 rounded font-mono text-gray-800 overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </details>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={this.handleRetry}
                  className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <RefreshCw size={18} className="mr-2" />
                  Recharger l'application
                </button>
                
                <button
                  onClick={this.handleGoHome}
                  className="flex-1 flex items-center justify-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  <Home size={18} className="mr-2" />
                  Retour à l'accueil
                </button>
              </div>

              {/* Help Text */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  Besoin d'aide ?
                </h3>
                <p className="text-sm text-blue-800 leading-relaxed">
                  Si le problème persiste, veuillez contacter le support technique en mentionnant 
                  l'ID d'erreur <code className="bg-blue-200 px-1 rounded text-xs">{errorId}</code>.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;